// Wire shapes (snake_case, as the API returns them) and the camelCase shapes the Studio UI reads.
// The server is the authority wherever the two differ; this module and its mappers are where the
// client adapts.

/* eslint-disable camelcase -- the wire shapes keep the API's snake_case field names. */

/** Derived server-side from the address domain. Never stored, never sent by the client. */
export type SeedProvider = 'Gmail' | 'Yahoo' | 'Outlook' | 'AOL' | 'iCloud' | 'Other';

/** What the API stores and accepts. */
export type SeedSourceValue = 'inbox_monster' | 'sparkpost' | 'other';

/** What the UI shows and keeps in the URL. */
export type SeedSourceLabel = 'Inbox Monster' | 'SparkPost' | 'Other';

export type SeededFilter = 'never' | 'gt30' | 'gt90';

export type SeedResponse = {
  id: string;
  email: string;
  provider: SeedProvider;
  source: SeedSourceValue;
  group_ids: string[];
  /** Null is a real state: never seeded. The API never sends `''`. */
  last_seeded_at: string | null;
  created_at: string;
};

export type SeedStatsResponse = {
  seed_count: number;
  group_count: number;
  never_seeded_count: number;
};

export type SeedFacetsResponse = {
  provider: Record<SeedProvider, number>;
  source: Record<SeedSourceValue, number>;
  /** Group uuid -> live member count. */
  group: Record<string, number>;
  seeded: Record<SeededFilter, number>;
};

/** The list envelope, with `stats` and `facets` carried as extra fields next to `total`. */
export type SeedListEnvelope = {
  data: SeedResponse[];
  data_count: number;
  total: number;
  limit: number;
  offset: number;
  last_item_identifier: number | null;
  stats: SeedStatsResponse;
  facets: SeedFacetsResponse;
};

export type DeleteSeedsResponse = {
  deleted_count: number;
  /** Groups that lost at least one member, so their counts can be refreshed. */
  affected_group_ids: string[];
  /** ISO. The server keeps a deleted seed restorable until this moment. */
  restore_expires_at: string;
};

export type RestoreSeedsResponse = {
  restored_count: number;
  /** Deleted too long ago to come back. */
  expired_count: number;
  seeds: SeedResponse[];
};

export type SeedIdsResponse = { ids: string[]; count: number };

/** How one CSV import landed. `seeds` holds only the rows the import created or restored. */
export type ImportSeedsResponse = {
  created: number;
  restored: number;
  /** Addresses that were already live seeds. Not an error: the file simply overlapped the list. */
  skipped_existing: number;
  /** Addresses the server accepted into the request and the database would not take. */
  invalid: string[];
  seeds: SeedResponse[];
  /** Only when the import named a group. Every live seed for the file's addresses is now a member. */
  group?: ImportSeedsGroupResponse;
};

/** Where an import's addresses landed when the request named a group. */
export type ImportSeedsGroupResponse = {
  id: string;
  name: string;
  added_count: number;
  already_member_count: number;
};

/** One seed as the Studio pages read it. Structurally the prototype's `Seed`. */
export type Seed = {
  id: string;
  email: string;
  provider: SeedProvider;
  source: SeedSourceLabel;
  groupIds: string[];
  lastSeededAt: string | null;
};

export type SeedStats = {
  seedCount: number;
  groupCount: number;
  neverSeededCount: number;
};

export type SeedFacets = {
  provider: Record<SeedProvider, number>;
  source: Record<SeedSourceLabel, number>;
  group: Record<string, number>;
  seeded: Record<SeededFilter, number>;
};

export type SeedList = {
  seeds: Seed[];
  total: number;
  limit: number;
  offset: number;
  lastItemIdentifier: number | null;
  stats: SeedStats;
  facets: SeedFacets;
};

/** One group as the list returns it. The list never carries `seed_ids`; the detail does. */
export type SeedGroupRowResponse = {
  id: string;
  name: string;
  description: string | null;
  member_count: number;
  isp_mix: Record<SeedProvider, number>;
  updated_at: string;
};

export type SeedGroupDetailResponse = SeedGroupRowResponse & { seed_ids: string[] };

/** What a group delete releases. The seeds stay on the list; only the grouping goes away. */
export type DeleteGroupResponse = {
  deleted: true;
  released_seed_count: number;
};

/**
 * How an "Add to group" landed. Neither `already_member_count` nor `skipped_missing_count` is an
 * error: the selection can overlap the group, and it can outlive a seed someone else deleted.
 */
export type AddSeedsToGroupResponse = {
  added_count: number;
  already_member_count: number;
  /** Selected ids that no longer resolve to a live seed. Skipped by the server, not refused. */
  skipped_missing_count: number;
  group: SeedGroupRowResponse;
};

export type SeedGroupStatsResponse = {
  group_count: number;
  grouped_seed_count: number;
  ungrouped_seed_count: number;
};

export type SeedGroupListEnvelope = {
  data: SeedGroupRowResponse[];
  data_count: number;
  total: number;
  limit: number;
  offset: number;
  last_item_identifier: number | null;
  stats: SeedGroupStatsResponse;
};

/** One group as the Groups page reads it. */
export type SeedGroupSummary = {
  id: string;
  name: string;
  description: string | null;
  memberCount: number;
  ispMix: Record<SeedProvider, number>;
  updatedAt: string;
};

/** The group the editor opens: the summary plus the membership it is about to replace. */
export type SeedGroupDetail = SeedGroupSummary & { seedIds: string[] };

export type SeedGroupStats = {
  groupCount: number;
  groupedSeedCount: number;
  ungroupedSeedCount: number;
};

export type SeedGroupList = {
  groups: SeedGroupSummary[];
  total: number;
  limit: number;
  offset: number;
  lastItemIdentifier: number | null;
  stats: SeedGroupStats;
};

/**
 * The data sources that read a tracking header, in the order the server lists them.
 * `sparkpost` is the wire word for SparkPost Email Analyst (eDataSource).
 */
export const SEED_TRACKING_HEADER_SOURCES = ['inbox_monster', 'sparkpost'] as const;

export type SeedTrackingHeaderSource = (typeof SEED_TRACKING_HEADER_SOURCES)[number];

export type SeedTrackingHeaderResponse = {
  source: SeedTrackingHeaderSource;
  name: string;
  updated_by: string | null;
  updated_at: string | null;
};

/** The Settings tab, as the API answers it. */
export type SeedSettingsResponse = {
  tracking_headers: SeedTrackingHeaderResponse[];
  value_format_example: string;
  value_max_length: number;
};

export type SeedTrackingHeader = {
  source: SeedTrackingHeaderSource;
  /** `null` when the answer carries no entry for this source, never `undefined`. */
  name: string | null;
  updatedBy: string | null;
  updatedAt: string | null;
};

export type SeedSettings = {
  /** One entry per data source, keyed by source so a reader needs no lookup. */
  trackingHeaders: Record<SeedTrackingHeaderSource, SeedTrackingHeader>;
  valueFormatExample: string;
  /**
   * The longest tracking value the wizard may propose in THIS environment.
   *
   * The column stores the value with its stage prefix, so a proposal has four characters less room
   * outside production. Only the server knows the stage, which is why this travels rather than
   * being a constant here: capping at the column width would let the wizard accept a value the
   * server then refuses.
   */
  valueMaxLength: number;
};

/** The Inbox Monster sync diff, computed server-side. */
export type ProviderSeedlistResponse = {
  /** How many addresses the vendor lists, deduplicated: two rows differing only in case are one. */
  provider_count: number;
  /** Vendor addresses this domain would refuse on the way in, so they are never offered. */
  provider_invalid_count: number;
  fetched_at: string;
  /** Null before the first sync this environment ever applied. */
  last_synced_at: string | null;
  /** Vendor addresses no live Pray seed holds. */
  to_add: string[];
  /** Live seeds whose source is `inbox_monster` and which the vendor no longer lists. */
  to_remove: { seed_id: string; email: string }[];
};

export type ProviderSeedlist = {
  providerCount: number;
  providerInvalidCount: number;
  fetchedAt: string;
  lastSyncedAt: string | null;
  toAdd: string[];
  toRemove: { seedId: string; email: string }[];
};

/** How one applied sync landed. */
export type SyncSeedsResponse = {
  added: number;
  /** Addresses the vendor lists that Pray already had. Not a refusal: the diff simply aged. */
  skipped_existing: number;
  /** Addresses the server accepted into the request and the database would not take. */
  invalid: string[];
  removed: number;
  /** Exactly what was soft-deleted, so Undo asks for these and not for what was requested. */
  removed_seed_ids: string[];
  /** Requested removals the sync was not entitled to make, or that were already gone. */
  skipped_removals: number;
  /** True when the removals died partway. The additions still stand. */
  removal_failed: boolean;
  synced_at: string;
  /** ISO, or null when nothing was removed. The window matches a manual delete's. */
  restore_expires_at: string | null;
};

// --- Wizard step 1 -----------------------------------------------------------

/** The three campaign types the wizard can seed. The fourth, `transactional`, has a registry. */
export type SeedCampaignType = 'email_campaign' | 'recurring_campaign' | 'warmup_campaign';

/**
 * What a declared field MEANS, which is what picks the control the wizard draws.
 *
 * Never the JavaScript type the emitter passes: `taxes` reaches SendGrid as the string `'0.00'`
 * from Stripe and as the number `0` from Bango on the same template, and both are `currency` here.
 * The wire carries a plain string and the mapper narrows it. A type this bundle has never heard
 * of, or none at all, becomes `string` there, so a server ahead of or behind this bundle still
 * answers a working form instead of an empty one.
 */
export type SeedTemplateFieldType =
  | 'string'
  | 'number'
  | 'currency'
  | 'date'
  | 'datetime'
  | 'url'
  | 'enum'
  | 'boolean'
  | 'json';

/** `date` and `datetime` only: which of the 4 shapes the real emitter produces. */
export type SeedTemplateDateFormat = 'us_slash' | 'long_day' | 'long_day_padded' | 'iso_datetime';

/** One value the admin types as sample data before a transactional run renders. */
export type SeedTemplateFieldResponse = {
  name: string;
  /** The human label the wizard prints above the input. */
  label?: string;
  /** Absent from an older server; the field then reads as text. */
  type?: string;
  /** The text the REAL emitter produces, which is what the input shows before anything is typed. */
  placeholder?: string;
  required: boolean;
  /** `enum` only: every value the emitter can write. */
  values?: string[];
  format?: string;
  /**
   * The emitter writes the key on every send and the VALUE may be null.
   *
   * Absent from an older server, which is the unblocking direction: the field then
   * reads as an ordinary required one.
   */
  nullable?: boolean;
  /**
   * The emitter always writes this exact value, so the field is stated and never asked.
   *
   * The run writes it from the schema and ignores anything a client sends for that key, so the row
   * is display only: it tells a reader what the real email carries without offering to change it.
   */
  constant?: string;
};

export type SeedTemplateSender = {
  email: string;
  name?: string;
};

/** One entry of the transactional templates registry, as the server holds it. */
export type SeedTemplateResponse = {
  /** The lockbox name, e.g. `SENDGRID_TEMPLATE_MAGIC_LINK`. It is what a run records. */
  key: string;
  label: string;
  /** Null when this environment configures no id for the name, so nothing can send it. */
  template_id: string | null;
  /** The sender override the real send passes. Null means it leaves from the lambda default. */
  sender: SeedTemplateSender | null;
  dynamic_fields: SeedTemplateFieldResponse[];
};

/** One declared field, narrowed by the mapper so the wizard can switch on `type` exhaustively. */
export type SeedTemplateField = {
  name: string;
  label: string;
  type: SeedTemplateFieldType;
  placeholder: string;
  required: boolean;
  values?: string[];
  format?: SeedTemplateDateFormat;
  /**
   * The emitter writes the key on every send, and a blank answer travels as a real `null`.
   *
   * Only `required` AND `nullable` together mean "the admin may leave this empty": `required`
   * says the key always travels and `nullable` says the value need not. 3 fields carry it, and
   * `billed_carrier` is why — its callers pass `null` twice and a merchant account key once, so
   * refusing a blank left the trial-started template unsendable.
   */
  nullable?: boolean;
  /** Set on a field the emitter always writes itself. The wizard states it and never asks. */
  constant?: string;
};

export type SeedTemplate = {
  key: string;
  label: string;
  templateId: string | null;
  sender: SeedTemplateSender | null;
  dynamicFields: SeedTemplateField[];
};

export type SeedCampaignCatalogResponse = {
  slug: string;
  /** The catalog's uuid, which is what the catalog items endpoint takes. */
  catalog_id: string;
  name: string;
  fields: string[];
};

export type SeedCampaignReservedSegmentResponse = {
  slug: string;
  name: string;
  catalog_slugs: string[];
};

export type SeedCampaignDetailResponse = {
  id: string;
  name: string;
  slug: string;
  subject: string | null;
  preview_text: string | null;
  sender_from: string | null;
  status: string | null;
  last_sent_at: string | null;
  tokens: string[];
  tokens_truncated: boolean;
  catalogs: SeedCampaignCatalogResponse[];
  reserved_segment: SeedCampaignReservedSegmentResponse | null;
  has_magic_login_placeholder: boolean;
};

export type SeedCampaignCatalog = {
  slug: string;
  catalogId: string;
  name: string;
  fields: string[];
};

export type SeedCampaignReservedSegment = {
  slug: string;
  name: string;
  catalogSlugs: string[];
};

export type SeedCampaignDetail = {
  id: string;
  name: string;
  slug: string;
  subject: string | null;
  previewText: string | null;
  senderFrom: string | null;
  status: string | null;
  lastSentAt: string | null;
  tokens: string[];
  /** True when the template asks more questions than one step renders; the tail is missing. */
  tokensTruncated: boolean;
  catalogs: SeedCampaignCatalog[];
  reservedSegment: SeedCampaignReservedSegment | null;
  hasMagicLoginPlaceholder: boolean;
};

/** The four run types. `transactional` sends a SendGrid template id and values instead of a body. */
export type SeedRunType = 'transactional' | SeedCampaignType;

/** One chosen record, in the UI's own casing. `postPreview` maps it to the wire names. */
export type SeedPreviewCatalogSelection = {
  slug: string;
  catalogId: string;
  key: string;
};

export type SeedPreviewResponse = {
  from: string | null;
  to: string | null;
  subject: string | null;
  preview: string | null;
  html: string | null;
  is_rendered: boolean;
  unavailable_reason: string | null;
  template_id: string | null;
  dynamic_template_data: Record<string, unknown> | null;
};

/**
 * The email as the backend will send it.
 *
 * `isRendered` false is not an error: a transactional template, a campaign with no compiled body
 * and a catalog row missing for the chosen date all answer a reason the card prints instead of the
 * frame.
 */
export type SeedPreview = {
  from: string | null;
  to: string | null;
  subject: string | null;
  previewText: string | null;
  html: string | null;
  isRendered: boolean;
  unavailableReason: string | null;
  /**
   * The transactional type only, and both are null for a campaign.
   *
   * A template is rendered by SendGrid, so the preview answers `isRendered: true` with no body and
   * names the id and the values instead. That is not a failed render, which is why the card reads
   * these two rather than branching on a null body.
   */
  templateId: string | null;
  dynamicTemplateData: Record<string, unknown> | null;
};

/**
 * The send cycle only: `sent` means the sender confirmed the send. Where the emails landed
 * is the metrics state, which never moves this.
 */
export type SeedRunStatus = 'queued' | 'sending' | 'sent' | 'failed';

/**
 * Where the metrics of a run stand, one aggregate over the per-vendor entries.
 * `not_applicable` is a run no vendor measures: none of its seeds is a vendor mailbox, or it
 * closed without one accepted message.
 */
export type SeedRunMetricsState = 'not_applicable' | 'pending' | 'partial' | 'complete' | 'no_report';

/** The vendors that measure a run, in the order the server lists them. */
export type SeedMetricsVendor = 'inbox_monster' | 'sparkpost';

/** One vendor's own state. A vendor never reads `not_applicable`: it is absent instead. */
export type SeedRunMetricsSourceState = Exclude<SeedRunMetricsState, 'not_applicable'>;

export type SeedRunMetricsSourceResponse = {
  vendor: SeedMetricsVendor;
  state: SeedRunMetricsSourceState;
  synced_at: string | null;
  /** A metrics reason code, for example `seed_run_header_name_mismatch`. Never a status. */
  last_error: string | null;
};

export type SeedRunMetricsResponse = {
  state: SeedRunMetricsState;
  /** Null only on a run with neither `sent_at` nor `dispatched_at`. */
  window_closes_at: string | null;
  /** Empty with `state: 'not_applicable'` is "No metrics to collect". */
  sources: SeedRunMetricsSourceResponse[];
};

export type SeedRunMetricsSource = {
  vendor: SeedMetricsVendor;
  state: SeedRunMetricsSourceState;
  syncedAt: string | null;
  lastError: string | null;
};

export type SeedRunMetrics = {
  state: SeedRunMetricsState;
  windowClosesAt: string | null;
  sources: SeedRunMetricsSource[];
};

/** The ISP keys SparkPost Email Analyst (eDataSource) reports under. `other` is its long tail. */
export type SeedSparkPostIspKey = 'gmail' | 'outlook' | 'yahoo' | 'icloud' | 'aol' | 'other';

export type SeedRunSparkPostIspCountsResponse = {
  inbox: number;
  spam: number;
  missing: number;
};

/**
 * The SparkPost Email Analyst numbers of a run, as the server stores them. Null on the detail when
 * the run has no `sparkpost` entry or no stored report.
 *
 * The rates are fractions (0.9 is 90 %), as eDataSource sends them. `panel_volume` is inbox plus
 * spam on the vendor's own panel, which is not this run's seed list.
 */
export type SeedRunSparkPostResponse = {
  panel_volume: number;
  inbox_rate: number;
  spam_rate: number;
  synced_at: string | null;
  isp_breakdown: Partial<Record<SeedSparkPostIspKey, SeedRunSparkPostIspCountsResponse>>;
};

export type SeedRunSparkPostIspResult = {
  isp: SeedProvider;
  inbox: number;
  spam: number;
  missing: number;
};

export type SeedRunSparkPost = {
  panelVolume: number;
  /** A fraction, 0 to 1. */
  inboxRate: number;
  /** A fraction, 0 to 1. */
  spamRate: number;
  syncedAt: string | null;
  /** One row per ISP key the report carries, in a fixed order. A key it does not carry has no row. */
  ispResults: SeedRunSparkPostIspResult[];
};

/** How a transactional run's N messages were answered for. Null on the campaign types. */
export type SeedRunSendOutcomes = {
  accepted: number;
  rejected: number;
  pending: number;
};

export type SeedRunReportSummaryResponse = {
  inbox_count: number;
  spam_count: number;
  missing_count: number;
  seen_count: number;
  total_count: number;
};

export type SeedRunCatalogSelectionResponse = {
  slug: string;
  catalog_id: string;
  key: string;
  /** Absent on a run older than the server's record labels; `runMapper` names those rows by their Key. */
  label?: string;
};

/** One authentication check, as the vendor counts it across the whole panel. */
export type SeedRunAuthCheck = {
  pass: number;
  fail: number;
};

/**
 * One ISP row of the report.
 *
 * `folderPlacements` is Gmail's tab split and only Gmail sends it; every other ISP reports no
 * tabs at all, which is why it is nullable rather than a pair of zeros.
 */
export type SeedRunIspResult = {
  isp: SeedProvider;
  total: number;
  inbox: number;
  spam: number;
  missing: number;
  folderPlacements: { primary: number; promotions: number } | null;
};

/**
 * Engagement on the seed panel, never on real readers.
 *
 * EVERY counter is nullable, not only `uniqueOpens`. The vendor has no field for unique opens at
 * all, and it may report one bucket and not the one beside it; the server stores a counter it did
 * not report as null rather than as a zero. The tab hides a null line instead of drawing a
 * measurement nobody made.
 */
export type SeedRunEngagement = {
  impressions: number | null;
  uniqueOpens: number | null;
  averageReadSeconds: number | null;
  shortRead: number | null;
  mediumRead: number | null;
  longRead: number | null;
  desktop: number | null;
  mobile: number | null;
};

/**
 * The placement report of one run, as the Placement tab reads it.
 *
 * `seenCount` is inbox plus spam. It is the denominator while the Inbox Monster metrics are open:
 * dividing a partial panel by the total buries the seeds nobody has looked at yet in "missing".
 */
export type SeedRunReport = {
  totalCount: number;
  seenCount: number;
  inboxCount: number;
  spamCount: number;
  missingCount: number;
  spf: SeedRunAuthCheck;
  dkim: SeedRunAuthCheck;
  dmarc: SeedRunAuthCheck;
  firstSeenAt: string | null;
  lastSeenAt: string | null;
  ispResults: SeedRunIspResult[];
  engagement: SeedRunEngagement | null;
};

/** The vendor's own summary. `isGenerating` is true while it is still being written. */
export type SeedRunAiSummary = {
  text: string;
  isGenerating: boolean;
};

export type SeedRunDetailResponse = SeedRunRowResponse & {
  artist_id: string | null;
  occurrence_date: string | null;
  catalog_selections: SeedRunCatalogSelectionResponse[];
  replacements: Record<string, string>;
  custom_headers: { name: string; value: string }[];
  headers: Record<string, string>;
  seed_ids: string[];
  isp_mix: Record<SeedProvider, number>;
  /** The SparkPost Email Analyst header name. Null on a run older than the per-source header names. */
  sparkpost_header_name: string | null;
  dispatched_at: string | null;
  /** When the sender confirmed the send. Null while unconfirmed and on a `failed` run. */
  sent_at: string | null;
  dispatched_count: number;
  accepted_count: number | null;
  metrics: SeedRunMetricsResponse;
  sparkpost: SeedRunSparkPostResponse | null;
  send_outcomes: SeedRunSendOutcomes | null;
  sent: {
    from: string | null;
    subject: string | null;
    preview: string | null;
    html: string | null;
    template_id: string | null;
    dynamic_template_data: Record<string, unknown> | null;
  };
  /**
   * Typed `unknown` on purpose, exactly as the server types it.
   *
   * It is a JSON column the detail passes through without validating: a cell written before a
   * shape change, or by hand outside this domain, reaches the client as whatever it holds.
   * `runReportMapper` is the one narrowing, and an unreadable cell degrades to "no report yet".
   */
  report: unknown | null;
  ai_summary: { text: string; is_generating: boolean } | null;
};

/** The answer of "Sync metrics": the run detail plus what the attempt did. */
export type SeedRunRefreshResponse = SeedRunDetailResponse & {
  metrics_sync: {
    checked_count: number;
    metrics_updated_count: number;
    metrics_complete_count: number;
    metrics_no_report_count: number;
    unreadable_report_count: number;
    send_timeout_count: number;
    truncated_cycle: boolean;
    errors: { run_id: string; reason: string }[];
    closing_read_failures: { run_id: string; reason: string }[];
  };
};

export type SeedRunCatalogSelection = {
  slug: string;
  catalogId: string;
  key: string;
  /** How the record reads, named by the server from the row the render read. */
  label: string;
};

/**
 * One run as the Done page and the run detail read it.
 *
 * `recipientCount` and `ispMix` are the server's, computed from the groups as they stood at Send.
 * The wizard shows its own count before Send and this one after, and the two can legitimately
 * differ when a group moved in between.
 */
export type SeedRunDetail = SeedRunRow & {
  artistId: string | null;
  occurrenceDate: string | null;
  catalogSelections: SeedRunCatalogSelection[];
  replacements: Record<string, string>;
  customHeaders: { name: string; value: string }[];
  /** Tracking plus customs, flattened, as the preview card and "What was sent" show them. */
  headers: Record<string, string>;
  seedIds: string[];
  ispMix: Record<SeedProvider, number>;
  /** The SparkPost Email Analyst header name. Null on a run older than the per-source header names. */
  sparkpostHeaderName: string | null;
  dispatchedAt: string | null;
  sentAt: string | null;
  dispatchedCount: number;
  acceptedCount: number | null;
  metrics: SeedRunMetrics;
  /** The SparkPost Email Analyst numbers. Never added to the Inbox Monster report: two panels. */
  sparkpost: SeedRunSparkPost | null;
  sendOutcomes: SeedRunSendOutcomes | null;
  sent: {
    from: string | null;
    subject: string | null;
    preview: string | null;
    html: string | null;
    /** The transactional type only: what the run sent instead of a body. */
    templateId: string | null;
    dynamicTemplateData: Record<string, unknown> | null;
  };
  /** Null until Inbox Monster has seen at least one seed of this run. */
  report: SeedRunReport | null;
  aiSummary: SeedRunAiSummary | null;
};

/** One run as the LIST returns it. The detail adds to it; neither carries the rendered body. */
export type SeedRunRowResponse = {
  id: string;
  type: SeedRunType;
  campaign_id: string;
  campaign_name: string;
  campaign_slug: string | null;
  status: SeedRunStatus;
  metrics_state: SeedRunMetricsState;
  /** The Inbox Monster header name. */
  tracking_header_name: string;
  tracking_header_value: string;
  group_ids: string[];
  recipient_count: number;
  loose_count: number;
  started_at: string;
  started_by: { id: string; name: string };
  /** Addresses SendGrid suppressed. Null until a send path reports. A warning, never a failure. */
  suppressed_count: number | null;
  failure_code: string | null;
  failure_reason: string | null;
  report_summary: SeedRunReportSummaryResponse | null;
  last_refreshed_at: string | null;
};

export type SeedRunStatsResponse = {
  run_count: number;
  in_progress_count: number;
  /** Pooled across every run with a report. Null while none has one. */
  mean_inbox_rate: number | null;
};

export type SeedRunFacetsResponse = {
  type: Record<SeedRunType, number>;
  status: Record<SeedRunStatus, number>;
  metrics_state: Record<SeedRunMetricsState, number>;
};

export type SeedRunListEnvelope = {
  data: SeedRunRowResponse[];
  data_count: number;
  total: number;
  limit: number;
  offset: number;
  last_item_identifier: number | null;
  stats: SeedRunStatsResponse;
  facets: SeedRunFacetsResponse;
};

/** What the Inbox column shows. Null means the vendor has not reported on this run yet. */
export type SeedRunReportSummary = {
  inboxCount: number;
  spamCount: number;
  missingCount: number;
  /** Seeds Inbox Monster has actually seen. Below `totalCount` while a run is collecting. */
  seenCount: number;
  totalCount: number;
};

/** One run as the Runs list reads it. */
export type SeedRunRow = {
  id: string;
  type: SeedRunType;
  campaignId: string;
  campaignName: string;
  campaignSlug: string | null;
  status: SeedRunStatus;
  metricsState: SeedRunMetricsState;
  /** The Inbox Monster header name. */
  trackingHeaderName: string;
  trackingHeaderValue: string;
  groupIds: string[];
  recipientCount: number;
  looseCount: number;
  startedAt: string;
  startedBy: { id: string; name: string };
  suppressedCount: number | null;
  failureCode: string | null;
  failureReason: string | null;
  reportSummary: SeedRunReportSummary | null;
  lastRefreshedAt: string | null;
};

export type SeedRunStats = {
  runCount: number;
  inProgressCount: number;
  meanInboxRate: number | null;
};

export type SeedRunFacets = {
  type: Record<SeedRunType, number>;
  status: Record<SeedRunStatus, number>;
  metricsState: Record<SeedRunMetricsState, number>;
};

export type SeedRunList = {
  runs: SeedRunRow[];
  total: number;
  limit: number;
  offset: number;
  lastItemIdentifier: number | null;
  stats: SeedRunStats;
  facets: SeedRunFacets;
};

export type SeedRunRecipientResponse = {
  email: string;
  provider: SeedProvider;
  /** Null once the seed row is gone for good. It survives a soft delete. */
  seed_id: string | null;
  still_in_seed_list: boolean;
  /**
   * Empty means the seed was picked loose rather than through a group. `name` is null when the
   * group no longer resolves; the id stays, so a recipient a group brought in never reads as a
   * loose pick because the group was renamed away.
   */
  via_groups: { id: string; name: string | null }[];
};

export type SeedRunRecipientStatsResponse = {
  total: number;
  loose_count: number;
  isp_mix: Record<SeedProvider, number>;
};

export type SeedRunRecipientListEnvelope = {
  data: SeedRunRecipientResponse[];
  data_count: number;
  total: number;
  limit: number;
  offset: number;
  last_item_identifier: number | null;
  stats: SeedRunRecipientStatsResponse;
};

/** One seed a run reached, as the Recipients tab reads it. */
export type SeedRunRecipient = {
  email: string;
  provider: SeedProvider;
  seedId: string | null;
  stillInSeedList: boolean;
  viaGroups: { id: string; name: string | null }[];
};

export type SeedRunRecipientStats = {
  total: number;
  looseCount: number;
  ispMix: Record<SeedProvider, number>;
};

export type SeedRunRecipientList = {
  recipients: SeedRunRecipient[];
  total: number;
  limit: number;
  offset: number;
  lastItemIdentifier: number | null;
  stats: SeedRunRecipientStats;
};
